/**
 * K3 Component Depreciation Section
 *
 * Komponentavskrivning enligt K3 kapitel 17
 * Obligatorisk för byggnader > 1 MSEK
 */

import { useState, useEffect } from "react";
import {
  Building2,
  Plus,
  Trash2,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronUp,
  Copy,
} from "lucide-react";
import { formatNumber } from "@/lib/format";
import type { UserInput } from "@/lib/api";

// Threshold for requiring component depreciation (1 MSEK)
const COMPONENT_DEPRECIATION_THRESHOLD = 1_000_000;

interface BuildingComponent {
  name: string;
  percentOfTotal: number;
  usefulLife: number;
  acquisitionValue: number;
}

interface Building {
  id: string;
  name: string;
  acquisitionDate?: string;
  totalAcquisitionValue: number;
  components: BuildingComponent[];
}

interface K3ComponentDepreciationSectionProps {
  userInput: UserInput;
  buildingsValue: number; // Total buildings value from SIE (konto 11xx)
  onChange: (componentDepreciation: UserInput["componentDepreciation"]) => void;
}

// Standard templates for building components
const BUILDING_TEMPLATES = {
  office: {
    name: "Kontorsbyggnad",
    components: [
      { name: "Stomme och grund", percentOfTotal: 55, usefulLife: 50 },
      { name: "Tak", percentOfTotal: 12, usefulLife: 25 },
      { name: "Fasad", percentOfTotal: 10, usefulLife: 30 },
      { name: "Fönster och dörrar", percentOfTotal: 8, usefulLife: 20 },
      { name: "VVS-installationer", percentOfTotal: 8, usefulLife: 15 },
      { name: "El-installationer", percentOfTotal: 5, usefulLife: 20 },
      { name: "Övrigt (målning, golv)", percentOfTotal: 2, usefulLife: 10 },
    ],
  },
  industrial: {
    name: "Industribyggnad",
    components: [
      { name: "Stomme och grund", percentOfTotal: 60, usefulLife: 40 },
      { name: "Tak", percentOfTotal: 15, usefulLife: 20 },
      { name: "Industriportar och dörrar", percentOfTotal: 10, usefulLife: 15 },
      { name: "El- och VVS-installationer", percentOfTotal: 15, usefulLife: 15 },
    ],
  },
  residential: {
    name: "Hyresfastighet",
    components: [
      { name: "Stomme och grund", percentOfTotal: 50, usefulLife: 100 },
      { name: "Tak", percentOfTotal: 10, usefulLife: 30 },
      { name: "Fasad", percentOfTotal: 10, usefulLife: 40 },
      { name: "Fönster", percentOfTotal: 8, usefulLife: 25 },
      { name: "Hiss", percentOfTotal: 5, usefulLife: 25 },
      { name: "Stammar (VVS)", percentOfTotal: 7, usefulLife: 50 },
      { name: "Övriga VVS-installationer", percentOfTotal: 5, usefulLife: 20 },
      { name: "El-installationer", percentOfTotal: 5, usefulLife: 30 },
    ],
  },
};

function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

export function K3ComponentDepreciationSection({
  userInput,
  buildingsValue,
  onChange,
}: K3ComponentDepreciationSectionProps) {
  const [enabled, setEnabled] = useState(
    userInput.componentDepreciation?.enabled ?? buildingsValue >= COMPONENT_DEPRECIATION_THRESHOLD
  );
  const [buildings, setBuildings] = useState<Building[]>(() => {
    if (userInput.componentDepreciation?.buildings?.length) {
      return userInput.componentDepreciation.buildings;
    }
    // Default: one building if buildings exist
    if (buildingsValue > 0) {
      return [
        {
          id: generateId(),
          name: "Byggnad 1",
          totalAcquisitionValue: buildingsValue,
          components: [],
        },
      ];
    }
    return [];
  });
  const [expandedBuilding, setExpandedBuilding] = useState<string | null>(
    buildings[0]?.id || null
  );
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");

  // Sync changes to parent
  useEffect(() => {
    const componentDepreciation: UserInput["componentDepreciation"] = {
      enabled,
      buildings: buildings.map((b) => ({
        ...b,
        components: b.components.map((c) => ({
          ...c,
          acquisitionValue: (c.percentOfTotal / 100) * b.totalAcquisitionValue,
        })),
      })),
    };
    onChange(componentDepreciation);
  }, [enabled, buildings, onChange]);

  // Check if component depreciation is required
  const isRequired = buildingsValue >= COMPONENT_DEPRECIATION_THRESHOLD;

  // Add new building
  const addBuilding = () => {
    const newBuilding: Building = {
      id: generateId(),
      name: `Byggnad ${buildings.length + 1}`,
      totalAcquisitionValue: 0,
      components: [],
    };
    setBuildings([...buildings, newBuilding]);
    setExpandedBuilding(newBuilding.id);
  };

  // Update building
  const updateBuilding = (id: string, updates: Partial<Building>) => {
    setBuildings(
      buildings.map((b) => (b.id === id ? { ...b, ...updates } : b))
    );
  };

  // Remove building
  const removeBuilding = (id: string) => {
    setBuildings(buildings.filter((b) => b.id !== id));
  };

  // Apply template to building
  const applyTemplate = (buildingId: string, templateKey: string) => {
    const template = BUILDING_TEMPLATES[templateKey as keyof typeof BUILDING_TEMPLATES];
    if (!template) return;

    const building = buildings.find((b) => b.id === buildingId);
    if (!building) return;

    const components = template.components.map((c) => ({
      name: c.name,
      percentOfTotal: c.percentOfTotal,
      usefulLife: c.usefulLife,
      acquisitionValue: (c.percentOfTotal / 100) * building.totalAcquisitionValue,
    }));

    updateBuilding(buildingId, { components });
    setSelectedTemplate("");
  };

  // Add component to building
  const addComponent = (buildingId: string) => {
    const building = buildings.find((b) => b.id === buildingId);
    if (!building) return;

    const newComponent: BuildingComponent = {
      name: "",
      percentOfTotal: 0,
      usefulLife: 10,
      acquisitionValue: 0,
    };

    updateBuilding(buildingId, {
      components: [...building.components, newComponent],
    });
  };

  // Update component
  const updateComponent = (
    buildingId: string,
    componentIndex: number,
    updates: Partial<BuildingComponent>
  ) => {
    const building = buildings.find((b) => b.id === buildingId);
    if (!building) return;

    const newComponents = building.components.map((c, i) =>
      i === componentIndex ? { ...c, ...updates } : c
    );
    updateBuilding(buildingId, { components: newComponents });
  };

  // Remove component
  const removeComponent = (buildingId: string, componentIndex: number) => {
    const building = buildings.find((b) => b.id === buildingId);
    if (!building) return;

    const newComponents = building.components.filter((_, i) => i !== componentIndex);
    updateBuilding(buildingId, { components: newComponents });
  };

  // Calculate total percentage for a building
  const getTotalPercentage = (components: BuildingComponent[]) => {
    return components.reduce((sum, c) => sum + (c.percentOfTotal || 0), 0);
  };

  // Calculate weighted average useful life
  const getWeightedUsefulLife = (components: BuildingComponent[]) => {
    const totalPercent = getTotalPercentage(components);
    if (totalPercent === 0) return 0;
    return components.reduce(
      (sum, c) => sum + c.usefulLife * (c.percentOfTotal / totalPercent),
      0
    );
  };

  // Calculate annual depreciation for a building
  const getAnnualDepreciation = (building: Building) => {
    return building.components.reduce((sum, c) => {
      const componentValue = (c.percentOfTotal / 100) * building.totalAcquisitionValue;
      return sum + componentValue / c.usefulLife;
    }, 0);
  };

  if (!enabled && !isRequired) {
    return (
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="text-blue-600" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Komponentavskrivning</h3>
              <p className="text-sm text-gray-500">K3 kap 17</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setEnabled(true)}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Aktivera
          </button>
        </div>
        <p className="text-sm text-gray-500 mt-3">
          Komponentavskrivning är frivilligt för er då byggnader inte överstiger 1 MSEK.
        </p>
      </div>
    );
  }

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Building2 className="text-blue-600" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Komponentavskrivning</h3>
            <p className="text-sm text-gray-500">K3 kap 17.4-17.5</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
          K3 {isRequired ? "Obligatorisk" : "Frivillig"}
        </span>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <div className="flex items-start gap-2">
          <Info size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-800">
            Enligt K3 17.4 ska väsentliga delar av en materiell anläggningstillgång skrivas av
            separat. I praktiken gäller detta framförallt <strong>byggnader över 1 MSEK</strong>.
          </p>
        </div>
      </div>

      {/* Buildings value summary */}
      <div className="bg-gray-50 rounded-lg p-3 mb-4">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Bokfört värde byggnader (konto 11xx)</span>
          <span className="font-bold text-gray-900">{formatNumber(buildingsValue)} kr</span>
        </div>
      </div>

      {/* Buildings list */}
      <div className="space-y-4">
        {buildings.map((building) => {
          const totalPercent = getTotalPercentage(building.components);
          const isBalanced = Math.abs(totalPercent - 100) < 0.1;
          const annualDep = getAnnualDepreciation(building);
          const weightedLife = getWeightedUsefulLife(building.components);
          const isExpanded = expandedBuilding === building.id;

          return (
            <div
              key={building.id}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              {/* Building header */}
              <div
                className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer"
                onClick={() => setExpandedBuilding(isExpanded ? null : building.id)}
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  <div>
                    <input
                      type="text"
                      value={building.name}
                      onChange={(e) => updateBuilding(building.id, { name: e.target.value })}
                      onClick={(e) => e.stopPropagation()}
                      className="font-medium text-gray-900 bg-transparent border-none p-0 focus:ring-0"
                    />
                    <p className="text-xs text-gray-500">
                      {building.components.length} komponenter · {formatNumber(annualDep)} kr/år
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!isBalanced && building.components.length > 0 && (
                    <span className="text-amber-600 text-xs">
                      {totalPercent.toFixed(1)}% ≠ 100%
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeBuilding(building.id);
                    }}
                    className="p-1 text-gray-400 hover:text-red-500"
                    disabled={buildings.length <= 1}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Building details */}
              {isExpanded && (
                <div className="p-4 space-y-4">
                  {/* Acquisition value */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Anskaffningsvärde
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          value={building.totalAcquisitionValue || ""}
                          onChange={(e) =>
                            updateBuilding(building.id, {
                              totalAcquisitionValue: Number(e.target.value) || 0,
                            })
                          }
                          className="input w-full pr-8"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                          kr
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Förvärvsdatum
                      </label>
                      <input
                        type="date"
                        value={building.acquisitionDate || ""}
                        onChange={(e) =>
                          updateBuilding(building.id, { acquisitionDate: e.target.value })
                        }
                        className="input w-full"
                      />
                    </div>
                  </div>

                  {/* Template selector */}
                  <div className="flex items-center gap-2">
                    <Copy size={16} className="text-gray-400" />
                    <select
                      value={selectedTemplate}
                      onChange={(e) => {
                        if (e.target.value) {
                          applyTemplate(building.id, e.target.value);
                        }
                      }}
                      className="input flex-1 text-sm"
                    >
                      <option value="">Välj en standardmall...</option>
                      <option value="office">Kontorsbyggnad</option>
                      <option value="industrial">Industribyggnad</option>
                      <option value="residential">Hyresfastighet</option>
                    </select>
                  </div>

                  {/* Components */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-700">Komponenter</h4>
                      <button
                        type="button"
                        onClick={() => addComponent(building.id)}
                        className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                      >
                        <Plus size={14} />
                        Lägg till
                      </button>
                    </div>

                    {building.components.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
                        Inga komponenter definierade. Välj en mall ovan eller lägg till manuellt.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {/* Header row */}
                        <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 px-1">
                          <div className="col-span-5">Komponent</div>
                          <div className="col-span-2 text-right">Andel (%)</div>
                          <div className="col-span-2 text-right">Livslängd (år)</div>
                          <div className="col-span-2 text-right">Värde</div>
                          <div className="col-span-1"></div>
                        </div>

                        {building.components.map((component, index) => (
                          <div
                            key={index}
                            className="grid grid-cols-12 gap-2 items-center"
                          >
                            <div className="col-span-5">
                              <input
                                type="text"
                                value={component.name}
                                onChange={(e) =>
                                  updateComponent(building.id, index, { name: e.target.value })
                                }
                                placeholder="Komponentnamn"
                                className="input w-full text-sm"
                              />
                            </div>
                            <div className="col-span-2">
                              <input
                                type="number"
                                value={component.percentOfTotal || ""}
                                onChange={(e) =>
                                  updateComponent(building.id, index, {
                                    percentOfTotal: Number(e.target.value) || 0,
                                  })
                                }
                                className="input w-full text-sm text-right"
                                step="0.1"
                              />
                            </div>
                            <div className="col-span-2">
                              <input
                                type="number"
                                value={component.usefulLife || ""}
                                onChange={(e) =>
                                  updateComponent(building.id, index, {
                                    usefulLife: Number(e.target.value) || 0,
                                  })
                                }
                                className="input w-full text-sm text-right"
                              />
                            </div>
                            <div className="col-span-2 text-right text-sm text-gray-600">
                              {formatNumber(
                                (component.percentOfTotal / 100) * building.totalAcquisitionValue
                              )}
                            </div>
                            <div className="col-span-1 text-right">
                              <button
                                type="button"
                                onClick={() => removeComponent(building.id, index)}
                                className="p-1 text-gray-400 hover:text-red-500"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        ))}

                        {/* Totals row */}
                        <div className="grid grid-cols-12 gap-2 pt-2 border-t border-gray-200 font-medium text-sm">
                          <div className="col-span-5">Summa</div>
                          <div
                            className={`col-span-2 text-right ${
                              isBalanced ? "text-green-600" : "text-amber-600"
                            }`}
                          >
                            {totalPercent.toFixed(1)}%
                          </div>
                          <div className="col-span-2 text-right text-gray-500">
                            ~{weightedLife.toFixed(0)} år
                          </div>
                          <div className="col-span-2 text-right">
                            {formatNumber(building.totalAcquisitionValue)}
                          </div>
                          <div className="col-span-1"></div>
                        </div>
                      </div>
                    )}

                    {/* Warning if not balanced */}
                    {!isBalanced && building.components.length > 0 && (
                      <div className="flex items-center gap-2 mt-2 text-sm text-amber-600">
                        <AlertTriangle size={14} />
                        <span>Komponenternas andelar ska summera till 100%</span>
                      </div>
                    )}
                  </div>

                  {/* Summary */}
                  {building.components.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Årlig avskrivning (totalt)</span>
                        <span className="font-bold text-gray-900">
                          {formatNumber(annualDep)} kr/år
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Viktad genomsnittlig nyttjandeperiod: {weightedLife.toFixed(0)} år
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add building button */}
      <button
        type="button"
        onClick={addBuilding}
        className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 mt-4"
      >
        <Plus size={16} />
        Lägg till byggnad
      </button>

      {/* Disable option (if not required) */}
      {!isRequired && enabled && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => setEnabled(false)}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Inaktivera komponentavskrivning
          </button>
        </div>
      )}
    </div>
  );
}
